import type { ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import type { TripDay, TripItineraryItem, TripItemWithoutDay } from 'tripPlanner/api/types'

interface Props {
  day: TripDay
  items: Array<TripItineraryItem>
  onViewDayItinerary?: (dayNumber: number) => void
}

interface ItinerarySummaryRowProps {
  indicator: ReactNode
  title: string
  subtitle: string
  previewImageIds: Array<string>
  extraImageCount: number
  onClick?: () => void
  secondaryAction?: ReactNode
}

const SummaryRowContainer = styled.div`
  align-items: center;
  display: flex;
  gap: ${rem(12)};
  width: 100%;
`

const SummaryRow = styled(Clickable)`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 0;
  display: flex;
  gap: ${rem(12)};
  justify-content: space-between;
  padding: ${rem(12)} 0;
  text-align: left;
  width: 100%;
`

const IndicatorBubble = styled.div`
  align-items: center;
  background: ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${props => props.theme.borderRadius.round};
  color: ${props => props.theme.palette.neutral.default.one};
  display: flex;
  flex-shrink: 0;
  height: ${rem(28)};
  justify-content: center;
  width: ${rem(28)};
`

const SummaryText = styled.div`
  flex: 1;
  min-width: 0;
`

const PreviewStack = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: ${rem(4)};
`

const PreviewTile = styled.div`
  border-radius: ${props => props.theme.borderRadius.L};
  height: ${rem(40)};
  overflow: hidden;
  position: relative;
  width: ${rem(40)};
`

const PreviewImage = styled(Image)`
  height: 100%;
  width: 100%;

  img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`

const ExtraCountOverlay = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  color: ${props => props.theme.palette.neutral.default.eight};
  display: flex;
  height: 100%;
  inset: 0;
  justify-content: center;
  position: absolute;
  width: 100%;
`

const PreviewChevron = styled.div`
  align-items: center;
  color: ${props => props.theme.palette.neutral.default.one};
  display: flex;
  flex-shrink: 0;
  justify-content: center;
`

function getItemImageId(item: TripItineraryItem | TripItemWithoutDay): string | undefined {
  const savedItemData = item.savedItemData
  const itemImageIds = savedItemData && 'imageIds' in savedItemData ? savedItemData.imageIds : undefined

  return itemImageIds?.[0] ?? item.itemPlaceDetail?.imageIds?.[0]
}

function getPreviewImages(items: Array<TripItineraryItem | TripItemWithoutDay>): Array<string> {
  const previewImages: Array<string> = []

  for (const item of items) {
    const imageId = getItemImageId(item)

    if (!imageId) {
      continue
    }

    previewImages.push(imageId)

    if (previewImages.length === 4) {
      break
    }
  }

  return previewImages
}

function ItinerarySummaryRow(props: ItinerarySummaryRowProps) {
  const { indicator, title, subtitle, previewImageIds, extraImageCount, onClick, secondaryAction } = props
  const intl = useIntl()

  const visiblePreviewImageIds = extraImageCount > 0 ? previewImageIds.slice(0, previewImageIds.length - 1) : previewImageIds
  const lastPreviewImageId = previewImageIds[previewImageIds.length - 1]

  return (
    <SummaryRowContainer>
      <SummaryRow onClick={onClick}>
        <Group direction="horizontal" gap={12} verticalAlign="center" fullWidth>
          <IndicatorBubble>{indicator}</IndicatorBubble>
          <SummaryText>
            <BodyText variant="medium" weight="bold">{title}</BodyText>
            <Caption variant="large" colour="neutral-three">{subtitle}</Caption>
          </SummaryText>
        </Group>
        <PreviewStack>
          {visiblePreviewImageIds.map((imageId, index) => (
            <PreviewTile key={`${imageId}-${index}`}>
              <PreviewImage id={imageId} fit="center" />
            </PreviewTile>
          ))}
          {extraImageCount > 0 && lastPreviewImageId && (
            <PreviewTile>
              <PreviewImage id={lastPreviewImageId} fit="center" />
              <ExtraCountOverlay>
                <BodyText variant="medium" weight="normal">
                  {intl.formatMessage(
                    {
                      id: 'aJKFcD79BqWApadMvSK7',
                      defaultMessage: '+{extraImageCount}',
                      description: 'Overlay label showing how many additional itinerary preview images are not displayed',
                    },
                    { extraImageCount },
                  )}
                </BodyText>
              </ExtraCountOverlay>
            </PreviewTile>
          )}
          <PreviewChevron>
            <LineAngleRightIcon size="S" />
          </PreviewChevron>
        </PreviewStack>
      </SummaryRow>
      {secondaryAction}
    </SummaryRowContainer>
  )
}

function ItineraryContent(props: Props) {
  const { day, items, onViewDayItinerary } = props
  const intl = useIntl()

  const activityCountLabel = intl.formatMessage(
    {
      id: 'lVc0WqIQ_aAar_LRK5_9',
      defaultMessage: '{count, plural, one {# activity} other {# activities}}',
      description: 'Activity count label shown in a condensed itinerary day summary row',
    },
    { count: items.length },
  )
  const previewImageIds = useMemo(() => getPreviewImages(items), [items])
  const extraImageCount = Math.max(items.length - previewImageIds.length, 0)

  const handleViewDayItineraryClick = useCallback(() => {
    onViewDayItinerary?.(day.dayNumber)
  }, [day.dayNumber, onViewDayItinerary])

  return (
    <ItinerarySummaryRow
      indicator={<BodyText variant="medium" weight="bold">{day.dayNumber}</BodyText>}
      title={day.title}
      subtitle={activityCountLabel}
      previewImageIds={previewImageIds}
      extraImageCount={extraImageCount}
      onClick={handleViewDayItineraryClick}
    />
  )
}

export { ItinerarySummaryRow, getPreviewImages }
export default ItineraryContent
